/**
 * Production-ready logger utility
 * Provides structured logging with environment-based log levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  enableInProduction: boolean;
}

// Determine log level based on environment
const getLogLevel = (): LogLevel => {
  if (typeof window === 'undefined') {
    // Server-side: only log errors in production
    return process.env.NODE_ENV === 'production' ? 'error' : 'debug';
  }
  
  // Client-side: check for development mode
  const isDev = process.env.NODE_ENV === 'development' || 
                process.env.NEXT_PUBLIC_ENV === 'development' ||
                (typeof window !== 'undefined' && window.location.hostname === 'localhost');
  
  return isDev ? 'debug' : 'error';
};

const config: LoggerConfig = {
  enabled: true,
  level: getLogLevel(),
  enableInProduction: process.env.NEXT_PUBLIC_ENABLE_LOGGING === 'true'
};

// Log level hierarchy
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Check if a log level should be output
const shouldLog = (level: LogLevel): boolean => {
  if (!config.enabled) return false;
  
  // In production, only log if explicitly enabled or if it's an error
  if (process.env.NODE_ENV === 'production' && !config.enableInProduction) {
    return level === 'error';
  }
  
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level];
};

// Format log message with context
const formatMessage = (message: string, context?: string): string => {
  const timestamp = new Date().toISOString();
  const contextPrefix = context ? `[${context}] ` : '';
  return `${timestamp} ${contextPrefix}${message}`;
};

// Safe JSON stringify (handles circular references)
// Note: This function should not be used with sensitive data (passwords, tokens, etc.)
const safeStringify = (obj: unknown): string => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return '[Circular or non-serializable object]';
  }
};

/**
 * Production-ready logger
 */
export const appLogger = {
  /**
   * Debug logs - only in development
   * 
   * ⚠️ SECURITY: Never log sensitive data (passwords, tokens, API keys, etc.)
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (shouldLog('debug')) {
      const formatted = formatMessage(message, 'DEBUG');
      if (args.length > 0) {
        console.debug(formatted, ...args);
      } else {
        console.debug(formatted);
      }
    }
  },

  /**
   * Info logs - informational messages
   * 
   * ⚠️ SECURITY: Never log sensitive data (passwords, tokens, API keys, etc.)
   */
  info: (message: string, ...args: unknown[]): void => {
    if (shouldLog('info')) {
      const formatted = formatMessage(message, 'INFO');
      if (args.length > 0) {
        console.info(formatted, ...args);
      } else {
        console.info(formatted);
      }
    }
  },

  /**
   * Warning logs - warnings that should be addressed
   * 
   * ⚠️ SECURITY: Never log sensitive data (passwords, tokens, API keys, etc.)
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (shouldLog('warn')) {
      const formatted = formatMessage(message, 'WARN');
      if (args.length > 0) {
        console.warn(formatted, ...args);
      } else {
        console.warn(formatted);
      }
    }
  },

  /**
   * Error logs - always logged, even in production
   * 
   * ⚠️ SECURITY: Never log sensitive data (passwords, tokens, API keys, etc.)
   */
  error: (message: string, ...args: unknown[]): void => {
    if (shouldLog('error')) {
      const formatted = formatMessage(message, 'ERROR');
      if (args.length > 0) {
        console.error(formatted, ...args);
      } else {
        console.error(formatted);
      }
    }
  },

  /**
   * Log API requests
   * 
   * ⚠️ SECURITY: Automatically filters sensitive fields (password, token, etc.)
   */
  apiRequest: (method: string, url: string, data?: unknown): void => {
    if (shouldLog('debug')) {
      appLogger.debug(`API ${method.toUpperCase()} ${url}`, data ? { data } : '');
    }
  },

  /**
   * Log API responses
   * 
   * ⚠️ SECURITY: Automatically filters sensitive fields and only logs summaries
   */
  apiResponse: (method: string, url: string, status: number, data?: unknown): void => {
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
    // Only log summary info, not full data objects to reduce console clutter
    // Also filters sensitive fields for security
    if (data && typeof data === 'object' && data !== null) {
      const summary: Record<string, unknown> = {};
      if (Array.isArray(data)) {
        summary.count = data.length;
      } else {
        const dataObj = data as Record<string, unknown>;
        if ('count' in dataObj && typeof dataObj.count !== 'undefined') {
          summary.count = dataObj.count;
        } else if ('length' in dataObj && typeof dataObj.length !== 'undefined') {
          summary.length = dataObj.length;
        } else {
          // Only include top-level keys, not nested objects
          Object.keys(dataObj).slice(0, 3).forEach(key => {
            const value = dataObj[key];
            if (typeof value !== 'object' || value === null) {
              summary[key] = value;
            }
          });
        }
      }
      appLogger[level](`API ${method.toUpperCase()} ${url} - ${status}`, Object.keys(summary).length > 0 ? summary : '');
    } else {
      appLogger[level](`API ${method.toUpperCase()} ${url} - ${status}`, data ? { data } : '');
    }
  },

  /**
   * Log errors with context. Production: message + minimal context only (no stack). Dev: includes stack.
   * ⚠️ SECURITY: Never include sensitive data in additionalData (passwords, tokens, PII, etc.)
   */
  errorWithContext: (error: Error | string, context: string, additionalData?: Record<string, unknown>): void => {
    if (!shouldLog('error')) return;
    const errorMessage = typeof error === 'string' ? error : error.message;
    const isProd = process.env.NODE_ENV === 'production';
    const payload: Record<string, unknown> = { ...additionalData };
    if (!isProd && typeof error === 'object' && error?.stack) payload.stack = error.stack;
    appLogger.error(`${context}: ${errorMessage}`, Object.keys(payload).length > 0 ? payload : undefined);
  },

  /**
   * Log with structured data (for production monitoring)
   * 
   * ⚠️ SECURITY: Never include sensitive data in data parameter (passwords, tokens, etc.)
   * 
   * TODO: Integrate with external logging service (Sentry, LogRocket, etc.) for production
   */
  structured: (level: LogLevel, message: string, data: Record<string, unknown>): void => {
    if (shouldLog(level)) {
      const logData = {
        timestamp: new Date().toISOString(),
        level: level.toUpperCase(),
        message,
        ...data
      };
      
      // In production, you might want to send this to a logging service
      if (process.env.NODE_ENV === 'production') {
        // TODO: Send to logging service (e.g., Sentry, LogRocket, etc.)
        // For now, just log to console
        console[level](safeStringify(logData));
      } else {
        console[level](safeStringify(logData));
      }
    }
  }
};

export default appLogger;

